import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { lazy, Suspense } from "react";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ProtectedRoute from "@/components/layout/ProtectedRoute";

// Lazy-load all section pages for better mobile performance
const DashboardOverview = lazy(() => import("@/pages/sections/DashboardOverview"));
const InventoryPage = lazy(() => import("@/pages/sections/InventoryPage"));
const PrintablesPage = lazy(() => import("@/pages/sections/PrintablesPage"));
const DocumentsPage = lazy(() => import("@/pages/sections/DocumentsPage"));
const EmployeeDocsPage = lazy(() => import("@/pages/sections/EmployeeDocsPage"));
const BillsPage = lazy(() => import("@/pages/sections/BillsPage"));
const SalesPage = lazy(() => import("@/pages/sections/SalesPage"));
const PayrollPage = lazy(() => import("@/pages/sections/PayrollPage"));
const SectionLanding = lazy(() => import("@/pages/sections/SectionLanding"));
const AdminSettingsPage = lazy(() => import("@/pages/sections/AdminSettingsPage"));
const ReconciliationPage = lazy(() => import("@/pages/sections/reconciliation/ReconciliationPage"));
const RecipeCostingPage = lazy(() => import("@/pages/sections/recipe-costing/RecipeCostingPage"));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      >
        <Route index element={<Suspense fallback={<LazyFallback />}><DashboardOverview /></Suspense>} />
        <Route path=":section" element={<Suspense fallback={<LazyFallback />}><SectionLanding /></Suspense>} />
        <Route path="inventory/:subPage" element={<Suspense fallback={<LazyFallback />}><InventoryPage /></Suspense>} />
        <Route path="printables/:subPage" element={<Suspense fallback={<LazyFallback />}><PrintablesPage /></Suspense>} />
        <Route path="documents/:branch/:subPage" element={<Suspense fallback={<LazyFallback />}><DocumentsPage /></Suspense>} />
        <Route path="documents/:branch" element={<Suspense fallback={<LazyFallback />}><DocumentsPage /></Suspense>} />
        <Route path="bills" element={<Suspense fallback={<LazyFallback />}><BillsPage /></Suspense>} />
        <Route path="bills/:branch/:subPage" element={<Suspense fallback={<LazyFallback />}><BillsPage /></Suspense>} />
        <Route path="bills/:branch" element={<Suspense fallback={<LazyFallback />}><BillsPage /></Suspense>} />
        <Route path="sales/:subPage" element={<Suspense fallback={<LazyFallback />}><SalesPage /></Suspense>} />
        <Route path="payroll/:subPage" element={<Suspense fallback={<LazyFallback />}><PayrollPage /></Suspense>} />
        <Route path="reconciliation" element={<Suspense fallback={<LazyFallback />}><ReconciliationPage /></Suspense>} />
        <Route path="reconciliation/:subPage" element={<Suspense fallback={<LazyFallback />}><ReconciliationPage /></Suspense>} />
        <Route path="recipe-costing" element={<Suspense fallback={<LazyFallback />}><RecipeCostingPage /></Suspense>} />
        <Route path="recipe-costing/:subPage" element={<Suspense fallback={<LazyFallback />}><RecipeCostingPage /></Suspense>} />
        <Route path="admin-settings" element={<Suspense fallback={<LazyFallback />}><AdminSettingsPage /></Suspense>} />
      </Route>
      {/* Redirect old branch routes to unified dashboard */}
      <Route path="/branch/*" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <SonnerToaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
